import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  InvestigatorFormData,
  InvestigatorDetails,
  CharacterBackstory,
  Equipment,
  CharacteristicValues,
  CharacteristicsMethod,
  Era,
  SocialClass,
  PulpData,
} from '@/types/investigator';
import { initialFormData } from '@/context/formInitialState';
import { getEraConfig } from '@/config/era-registry';

interface FormStore {
  formData: InvestigatorFormData;
  debugMode: boolean;
  preferredCharacteristicsMethod: CharacteristicsMethod;
  setDebugMode: (enabled: boolean) => void;
  updateDetails: (data: Partial<InvestigatorDetails>) => void;
  updateCharacteristics: (data: CharacteristicValues) => void;
  setCharacteristicsMethod: (method: CharacteristicsMethod) => void;
  updateQuickfireAssignments: (data: Record<string, number | null>) => void;
  updateOccupationSkills: (data: { [key: string]: number | string }) => void;
  updatePersonalSkills: (data: { [key: string]: number }) => void;
  updateSpecializationInputs: (data: { [key: string]: string }) => void;
  updateBackstory: (data: Partial<CharacterBackstory>) => void;
  updateEquipment: (data: Partial<Equipment>) => void;
  setCurrentStep: (step: number) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  setEra: (era: Era) => void;
  setSocialClass: (socialClass: SocialClass | undefined) => void;
  setPulpMode: (enabled: boolean) => void;
  updatePulp: (data: Partial<PulpData>) => void;
  resetFormData: () => void;
}

export const useFormStore = create<FormStore>()(
  persist(
    (set) => ({
      formData: initialFormData,
      debugMode: false,
      preferredCharacteristicsMethod: 'quickfire' as CharacteristicsMethod,

      setDebugMode: (enabled) =>
        set(() => ({
          debugMode: enabled,
        })),

      updateDetails: (newData) =>
        set((state) => ({
          formData: {
            ...state.formData,
            details: { ...state.formData.details, ...newData },
          },
        })),

      updateCharacteristics: (newData) =>
        set((state) => ({
          formData: { ...state.formData, characteristics: newData },
        })),

      setCharacteristicsMethod: (method) =>
        set((state) => ({
          formData: { ...state.formData, characteristicsMethod: method },
          preferredCharacteristicsMethod: method,
        })),

      updateQuickfireAssignments: (data) =>
        set((state) => ({
          formData: { ...state.formData, quickfireAssignments: data },
        })),

      updateOccupationSkills: (newData) =>
        set((state) => ({
          formData: { ...state.formData, occupationSkills: newData },
        })),

      updatePersonalSkills: (newData) =>
        set((state) => ({
          formData: { ...state.formData, personalSkills: newData },
        })),

      updateSpecializationInputs: (newData) =>
        set((state) => ({
          formData: { ...state.formData, specializationInputs: newData },
        })),

      updateBackstory: (newData) =>
        set((state) => ({
          formData: {
            ...state.formData,
            backstory: { ...state.formData.backstory, ...newData },
          },
        })),

      updateEquipment: (newData) =>
        set((state) => ({
          formData: {
            ...state.formData,
            equipment: { ...state.formData.equipment, ...newData },
          },
        })),

      setCurrentStep: (step) =>
        set((state) => ({
          formData: { ...state.formData, currentStep: step },
        })),

      goToNextStep: () =>
        set((state) => ({
          formData: {
            ...state.formData,
            currentStep: state.formData.currentStep + 1,
          },
        })),

      goToPreviousStep: () =>
        set((state) => ({
          formData: {
            ...state.formData,
            currentStep: Math.max(1, state.formData.currentStep - 1),
          },
        })),

      setEra: (era) =>
        set((state) => {
          const cfg = getEraConfig(era);
          // Respect era support flags from the registry rather than hardcoding 'pulp'.
          const pulpMode = !!cfg.pulpAlwaysOn || (!!cfg.supportsPulp && !!state.formData.pulpMode && cfg.pulpAlwaysOn !== false);
          // Default: Pulp turns off when switching to a fresh era unless that era forces it on.
          const finalPulpMode = !!cfg.pulpAlwaysOn;
          return {
            formData: {
              ...initialFormData,
              era,
              pulpMode: finalPulpMode,
              pulp: finalPulpMode
                ? { archetype: '', talents: [], archetypeBonusAllocations: {}, gadgets: [], psychicSkills: {} }
                : initialFormData.pulp,
              characteristicsMethod: state.preferredCharacteristicsMethod,
            },
          };
        }),

      setSocialClass: (socialClass) =>
        set((state) => ({
          formData: { ...state.formData, socialClass },
        })),

      setPulpMode: (enabled) =>
        set((state) => {
          const cfg = getEraConfig(state.formData.era);
          // Locked on for pulpAlwaysOn eras; locked off for eras that don't support it.
          const next = cfg.pulpAlwaysOn ? true : cfg.supportsPulp === false ? false : enabled;
          return {
          formData: {
            ...state.formData,
            pulpMode: next,
            pulp: next
              ? state.formData.pulp ?? {
                  archetype: '',
                  talents: [],
                  archetypeBonusAllocations: {},
                  gadgets: [],
                  psychicSkills: {},
                }
              : state.formData.pulp,
          },
          };
        }),

      updatePulp: (data) =>
        set((state) => ({
          formData: {
            ...state.formData,
            pulp: {
              archetype: '',
              talents: [],
              archetypeBonusAllocations: {},
              gadgets: [],
              psychicSkills: {},
              ...(state.formData.pulp ?? {}),
              ...data,
            },
          },
        })),

      resetFormData: () =>
        set((state) => ({
          formData: {
            ...initialFormData,
            characteristicsMethod: state.preferredCharacteristicsMethod,
          },
        })),
    }),
    {
      name: 'coc-character-form-data',
    }
  )
);

/** Drop-in replacement for the old useFormData context hook. */
export const useFormData = () => useFormStore();
